import React, { Fragment } from 'react';

// material-ui
import { Chip, IconButton, ListItemAvatar, ListItemButton, ListItemSecondaryAction, ListItemText, Stack, Typography } from '@mui/material';

import AssetLogo from '../AssetLogo';
import { McaRefi, McaRefiDto, McaRefiType, MerchantCashAdvance } from '../../graphql/types';
import { CurrencyText } from '../../components/input-formatters/CurrencyText';
import { DeleteOutlined } from '@ant-design/icons';

export interface McaRefisListOptionProps {
  refi: McaRefi;
  disabled: boolean;
  onDelete: (refi: McaRefi) => any;
}

function McaRefisListOption({ refi, onDelete, disabled }: McaRefisListOptionProps) {
  return (
    <Fragment>
      <ListItemButton>
        <ListItemAvatar sx={{ minWidth: 42 }}>
          <AssetLogo logo={refi?.merchantLogo} name={refi?.merchantLegalName} size={'sm'} />
        </ListItemAvatar>
        <ListItemText
          primary={
            <Stack component="span" direction="column" alignItems="left" justifyContent="space-between" spacing={0.5}>
              <Typography component="span" color="inherit" variant="caption">
                {`MCA #${refi.number}`}
              </Typography>
              <Stack direction={'row'} spacing={2} justifyContent="space-between">
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {refi?.merchantLegalName}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <CurrencyText value={refi.clearedBalance || 0} />
                </Typography>
              </Stack>
            </Stack>
          }
        />
        <ListItemSecondaryAction>
          <Stack
            mb={refi.type === McaRefiType.ClearedBalanceWithFees || refi.type === McaRefiType.PendingBalanceWithFees ? 5 : 2}
            direction={'row'}
            spacing={0.5}
          >
            <Chip color={'error'} label={<CurrencyText value={getRefiAmount(refi)} />} size="small" variant="light" />
            {!disabled && (
              <IconButton sx={{ paddingBottom: 2.5 }} color="secondary" onClick={() => onDelete(refi)}>
                <DeleteOutlined />
              </IconButton>
            )}
          </Stack>
        </ListItemSecondaryAction>
      </ListItemButton>
    </Fragment>
  );
}

export default McaRefisListOption;

export function getRefiAmount(refi: McaRefi) {
  if ((refi.amount || 0) > 0) {
    return refi.amount!;
  }
  switch (refi.type) {
    case McaRefiType.ClearedBalance:
      return refi.clearedBalance;
    case McaRefiType.ClearedBalanceWithFees:
      return refi.clearedBalance + refi.totalFees;
    case McaRefiType.PendingBalance:
      return refi.pendingBalance;
    case McaRefiType.PendingBalanceWithFees:
      return refi.pendingBalance + refi.totalFees;
    default:
      return 0;
  }
}

export function getRefiTypeAmount(type: McaRefiType, mca: MerchantCashAdvance | null | undefined) {
  if (!mca) return 0;
  switch (type) {
    case McaRefiType.ClearedBalance:
      return mca.balanceclr || 0;
    case McaRefiType.ClearedBalanceWithFees:
      return mca.balanceClrWFees || 0;
    case McaRefiType.PendingBalance:
      return mca.balancePnd || 0;
    case McaRefiType.PendingBalanceWithFees:
      return mca.balancePndWFees || 0;
    default:
      return 0;
  }
}

export function getRefisTotal(refis: McaRefi[] | McaRefiDto[] | undefined) {
  return (refis || []).reduce((total, refi) => {
    return getRefiAmount(refi) + total;
  }, 0);
}
